import { Box, Text } from '@chakra-ui/react'
import { LuActivity as Activity } from 'react-icons/lu'
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceArea,
  ResponsiveContainer,
} from 'recharts'
import SectionCard from './SectionCard'
import CustomTooltip from '../../../Estadisticas/components/CustomTooltip'
import { useChartTheme } from '../../../../hooks/useChartTheme'
import { brand, themeColors } from '../../../../theme'
import type { ProfilePoint, Stop } from '../../../../lib/trackAnalytics'

const colors = themeColors
const SPEED_AXIS = brand.greenLight // #2a8a87
const ALT_AXIS = '#3d6fa8'

interface Props {
  profile: ProfilePoint[]
  stops: Stop[]
  delay?: number
}

export default function SpeedProfileChart({ profile, stops, delay = 0 }: Props) {
  const { axis, grid } = useChartTheme()
  const hasAltitude = profile.some((p) => p.altitudeM != null)
  const lastIndex = profile.length - 1

  const tooltipFormatter = (value: number, name: string) => {
    if (name.startsWith('Altitud')) return `${Math.round(value)} m`
    return `${value.toFixed(1)} km/h`
  }

  return (
    <SectionCard
      title="Perfil de velocidad"
      subtitle={stops.length > 0 ? 'velocidad y altitud · zonas sombreadas = paradas' : 'velocidad y altitud a lo largo del recorrido'}
      icon={Activity}
      accent={SPEED_AXIS}
      delay={delay}
    >
      {profile.length < 2 ? (
        <Box py={10} textAlign="center">
          <Text fontSize="sm" color={colors.textMuted}>
            No hay suficientes puntos para graficar el perfil
          </Text>
        </Box>
      ) : (
        <Box position="relative" h="290px">
          {/* Rótulos de unidad horizontales, encima de cada eje (sin pisar los números) */}
          <Text
            position="absolute"
            top={0}
            left={2}
            fontSize="10px"
            fontWeight="700"
            color={SPEED_AXIS}
            zIndex={1}
            pointerEvents="none"
          >
            km/h
          </Text>
          {hasAltitude && (
            <Text
              position="absolute"
              top={0}
              right={2}
              fontSize="10px"
              fontWeight="700"
              color={ALT_AXIS}
              zIndex={1}
              pointerEvents="none"
            >
              m
            </Text>
          )}
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={profile} margin={{ top: 22, right: hasAltitude ? 6 : 12, left: 2, bottom: 4 }}>
              <defs>
                <linearGradient id="speedGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={SPEED_AXIS} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={SPEED_AXIS} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={grid.stroke} strokeDasharray={grid.strokeDasharray} vertical={false} />

              {/* Zonas de parada sombreadas */}
              {stops.map((s, i) => (
                <ReferenceArea
                  key={`stop-${i}`}
                  yAxisId="speed"
                  x1={s.startIndex}
                  x2={s.endIndex}
                  fill="#c8442f"
                  fillOpacity={0.08}
                  stroke="#c8442f"
                  strokeOpacity={0.2}
                />
              ))}

              <XAxis
                dataKey="index"
                type="number"
                domain={[0, lastIndex]}
                allowDecimals={false}
                stroke={axis.stroke}
                tick={axis.tick}
                tickLine={false}
                minTickGap={36}
                tickFormatter={(i: number) => profile[Math.round(i)]?.label ?? ''}
              />
              <YAxis
                yAxisId="speed"
                stroke={SPEED_AXIS}
                tick={{ ...axis.tick, fill: SPEED_AXIS }}
                tickLine={false}
                width={38}
              />
              {hasAltitude && (
                <YAxis
                  yAxisId="alt"
                  orientation="right"
                  stroke={ALT_AXIS}
                  tick={{ ...axis.tick, fill: ALT_AXIS }}
                  tickLine={false}
                  width={40}
                />
              )}
              <Tooltip
                content={<CustomTooltip formatter={tooltipFormatter} />}
                labelFormatter={(i) => profile[Math.round(Number(i))]?.label ?? ''}
              />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "'Instrument Sans', sans-serif", paddingTop: 4 }} />
              <Area
                yAxisId="speed"
                type="monotone"
                dataKey="speedKmh"
                name="Velocidad (km/h)"
                stroke={SPEED_AXIS}
                fill="url(#speedGradient)"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
              {hasAltitude && (
                <Line
                  yAxisId="alt"
                  type="monotone"
                  dataKey="altitudeM"
                  name="Altitud (m)"
                  stroke={ALT_AXIS}
                  strokeWidth={1.5}
                  strokeDasharray="5 3"
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}
    </SectionCard>
  )
}
